import { Component, OnInit } from '@angular/core';
import { SharedUtils, UserType } from '../../../shared/shared-utils';
import { Option } from '../../../shared/data-filters/data-filter-select/data-filter-select.component';
import { Client } from '../../../models/Client';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';
import { ClientsService } from '../../clients/shared/clients.service';
import { ProductService } from '../../../services/product.service';
import { process, GroupDescriptor } from '@progress/kendo-data-query';

class AppVersionsSearchCriteria {
  rentalPointAccountId?: string;
  clientId?: string;
}

@Component({
  selector: 'app-app-versions',
  templateUrl: './app-versions.component.html',
  styleUrl: './app-versions.component.scss',
  standalone: false
})
export class AppVersionsComponent implements OnInit {
    public filters: AppVersionsSearchCriteria = {};
    public currentUserType: UserType;
    public clientOptions: Option[];
    public rentalPointOptions: Option[];
    public currentLang: string;
    public showResultContent = false;
    public firstLoading = true;
    public chartData: {rentalPoint: string, deviceId: string, device: string, deviceType: string, installationId: string, installedVersion: string, lastUsedAt: any, lastUsedType: string, lastUsedContractId: string}[] = [];
    public gridData: any;
    public versionHistoryDialogOpen = false;
    public dialogTitle: string = "";
    public dialogData: any;
    public rawData: any;

    public groups: GroupDescriptor[] = [{ field: 'rentalPoint' }];

    private _clients: Client[];

    constructor(private _translate: TranslateService, private _clientsService: ClientsService, private _productService: ProductService) {
      this.currentLang = _translate.currentLang;
      this.clientOptions = [{ value: undefined, label: this._translate.instant('orders.bookingReservationOrders.all') }];
      this.rentalPointOptions = [{ value: undefined, label: this._translate.instant('orders.bookingReservationOrders.all') }];
      this.currentUserType = SharedUtils.getCurrentUserType();
    }

    ngOnInit(): void {
      let accountsRequest: Observable<Client> | Observable<Client[]>;
      switch (this.currentUserType.userTypeNumber) {
        case 1: // CLIENT
          accountsRequest = this._clientsService.getClient(undefined, this.currentUserType.accountId);
          break;
        case 2: // RENTALPOINT
          accountsRequest = this._clientsService.getClients();
          break;
        default:
          accountsRequest = this._clientsService.getClients();
          break;
      }

      forkJoin([accountsRequest]).subscribe(result => {
        if (this.currentUserType.userTypeNumber !== 2) {
          this.populateClientRentalPointOptions(result[0]);
        }
      });
    }

    populateClientRentalPointOptions(result: any) {
      switch (this.currentUserType.userTypeNumber) {
        case 1:
          result.rentalPoints.forEach((rentalPoint: any) => {
            this.rentalPointOptions.push({ value: rentalPoint.accountId, label: rentalPoint.rentalPointTranslation[this.currentLang] });
          });
          break;
        default:
          result.forEach((client: any) => {
            this.clientOptions.push({ value: client.id, label: client.businessName });
          });
          this._clients = result;
          break;
      }
    }

    initChart() {
      if (!this.filters.clientId) {
        return;
      }
      this._productService.getInstalledVersions(this.filters.clientId, this.filters.rentalPointAccountId).subscribe(res => {
        this.rawData = res;
        this.populateGridData(res);
      });
    }

    populateGridData(rawData: any) {
      this.chartData = [];
      rawData.forEach((rentalPoint: any) => {
        rentalPoint.devices.forEach((device: any) => {
          const rentalPointName = this.rentalPointOptions.find((r: any) => r.value === rentalPoint.id)?.label;
          // Installed version object
          const currentVersion = rentalPoint.installedVersions.find((v: any) => v.device.id === device.id);

          const deviceInfo = JSON.parse(device.deviceInfo);

          this.chartData.push({
            rentalPoint: rentalPointName ? rentalPointName : "",
            deviceId: device.deviceId,
            device: deviceInfo.name,
            deviceType: deviceInfo.manufacturer + " " + deviceInfo.model,
            installationId: device.installationId,
            installedVersion: currentVersion?.androidVersion,
            lastUsedAt: currentVersion?.lastUsedAt,
            lastUsedType: currentVersion?.lastUsedType,
            lastUsedContractId: currentVersion?.lastUsedContractId
          })
        });
      });

      this.showResultContent = true;
      this.gridData = process(this.chartData, { group: this.groups });
    }

    onClientSelectChange(clientId: number) {
      this.rentalPointOptions = [{ value: undefined, label: this._translate.instant('orders.bookingReservationOrders.all') }];
      if (clientId) {
        const selectedClient = this._clients.filter(client => client.id === clientId)[0];
        selectedClient.rentalPoints.forEach(rentalPoint => {
          this.rentalPointOptions.push({ value: rentalPoint.accountId, label: rentalPoint.rentalPointTranslation[this.currentLang] });
        });
      }
    }

    onResetButtonClick() {
      this.showResultContent = false;
      this.firstLoading = true;
      this.chartData = [];
      this.filters = {};
    }

    onApplyButtonClick() {
      this.showResultContent = false;
      this.initChart();
      if (this.firstLoading) {
        this.firstLoading = false;
      }
    }

    openVersionHistoryDialog(rentalPointName: any) {

      const rentalPoint = this.rentalPointOptions.find(r => r.label === rentalPointName);

      this.dialogTitle = rentalPointName;
      this.dialogData = [];
      const rentalPointData = this.rawData.find((r: any) => r.id === rentalPoint?.value);
      rentalPointData.installedVersionHistories.forEach((item: any) => {
        const deviceInfo = JSON.parse(item.deviceInfo);
        this.dialogData.push({
          deviceId: item.deviceId,
          device: deviceInfo.name,
          deviceType: deviceInfo.manufacturer + " " + deviceInfo.model,
          installation: item.installationId,
          androidVersion: item.androidVersion
        });
      });
      this.versionHistoryDialogOpen = true;
    }
}
